/**
 * Decode Fischer ski QR code data
 * Fischer 2025 skis have QR codes with the format:
 * [product_code];[length]/[serial_number];[HR]-[FA]
 * 
 * Example: 9002973236166;191/25519138337;2.2-103.0
 * - Product code: 9002973236166 (ignored)
 * - Length: 191 cm
 * - Serial number: 25519138337
 * - HR (camber height): 2.2
 * - FA (flex): 103.0
 */
export function decodeFischerQr(qrData) {
  if (!qrData || typeof qrData !== 'string') {
    return null;
  }

  const result = {
    serial_number: null,
    fa: null,
    hr: null,
    ski_length_cm: null,
    year_made: null,
    brand: 'Fischer',
    type: null, // classic or skate - may need to be inferred or set manually
  };

  // Try Fischer 2025 format: [product_code];[length]/[serial_number];[HR]-[FA]
  // Example: 9002973236166;191/25519138337;2.2-103.0
  const fischer2025Match = qrData.match(/^[^;]+;(\d+)\/(\d+);([\d.]+)-([\d.]+)$/);
  if (fischer2025Match) {
    result.ski_length_cm = fischer2025Match[1]; // Length in cm
    result.serial_number = fischer2025Match[2]; // Serial number
    result.hr = parseFloat(fischer2025Match[3]); // HR (camber height)
    result.fa = parseFloat(fischer2025Match[4]); // FA (flex)
    return result;
  }

  // Try to parse JSON format (fallback)
  try {
    const jsonData = JSON.parse(qrData);
    if (jsonData.serial_number) result.serial_number = jsonData.serial_number;
    if (jsonData.fa) result.fa = parseFloat(jsonData.fa);
    if (jsonData.hr) result.hr = parseFloat(jsonData.hr);
    if (jsonData.length) result.ski_length_cm = String(jsonData.length);
    if (jsonData.year) result.year_made = parseInt(jsonData.year);
    if (jsonData.type) result.type = jsonData.type;
    return result;
  } catch (e) {
    // Not JSON, continue with other formats
  }

  // Try URL format (e.g., https://fischer.com/ski/...)
  if (qrData.includes('fischer') || qrData.startsWith('http')) {
    // Extract serial number from URL if present
    const serialMatch = qrData.match(/serial[=:]?([A-Z0-9]+)/i) || qrData.match(/\/([A-Z0-9]{8,})\/?$/i);
    if (serialMatch) {
      result.serial_number = serialMatch[1];
    }
    return result;
  }

  // Try semicolon-separated format (more flexible parsing)
  if (qrData.includes(';')) {
    const parts = qrData.split(';').map(p => p.trim());
    
    // Check if second part has length/serial format: "191/25519138337"
    if (parts.length >= 2 && parts[1].includes('/')) {
      const lengthSerial = parts[1].split('/');
      if (lengthSerial.length === 2) {
        result.ski_length_cm = lengthSerial[0].trim();
        result.serial_number = lengthSerial[1].trim();
      }
    }
    
    // Check if third part has HR-FA format: "2.2-103.0"
    if (parts.length >= 3 && parts[2].includes('-')) {
      const hrFa = parts[2].split('-');
      if (hrFa.length === 2) {
        result.hr = parseFloat(hrFa[0].trim());
        result.fa = parseFloat(hrFa[1].trim());
      }
    }
    
    // If we got at least some data, return it
    if (result.serial_number || result.fa || result.hr || result.ski_length_cm) {
      return result;
    }
  }

  // Try comma-separated or pipe-separated format (fallback)
  const separators = [',', '|', '\t'];
  for (const sep of separators) {
    if (qrData.includes(sep)) {
      const parts = qrData.split(sep).map(p => p.trim());
      if (parts.length >= 1) result.serial_number = parts[0];
      if (parts.length >= 2) result.fa = parseFloat(parts[1]) || null;
      if (parts.length >= 3) result.hr = parseFloat(parts[2]) || null;
      if (parts.length >= 4) result.ski_length_cm = parts[3];
      if (parts.length >= 5) result.year_made = parseInt(parts[4]) || null;
      if (parts.length >= 6) result.type = parts[5].toLowerCase();
      return result;
    }
  }

  // If no structured data found, return null to indicate parsing failed
  return null;
}

